/**
 * What the plant did with a recommendation (migration 0196; the 2026-10-01 learning-loop plan,
 * L1 and L2). Every recommendation the platform shows carries a stable key (`recommendation_key`, built from
 * identifiers on the server, never from the words on the page): a brief's evidence action, a playbook action, a
 * suggested range, a data ask, a published study's action. A person records against it what the plant did, one event at
 * a time; the latest event is its state, which the briefs and a study serve beside the document
 * (`recommendation_states`).
 *
 *   POST /v1/twin/recommendation-events   {id?, key, text_shown, kind, plant_day?, scope?, baseline?, note?, via?}   capture.write
 *   GET  /v1/twin/recommendation-events?key=&kind=&since=&exclude_key_prefix=&latest=&limit=&offset=  newest first
 *   PUT  /v1/twin/recommendation-assignments   {key, text_shown, owner, due}   knowledge.respond (migration 0250)
 *   GET  /v1/twin/recommendation-assignments   the owners and due dates, and for an assigner the people to pick from
 *
 * The Go mirror is platform-core/internal/recommendations/store.go; change the two together.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { BRIEF_LIST_KEY, CADENCE_BRIEF_KEY, PLANT_BRIEF_KEY, useBriefLive } from "./brief";
import { STUDY_KEY } from "./studies";
import { ACTIONS_KEY } from "./actions";
import type { Paginated } from "./types";

/** What happened to a recommendation. */
export type RecommendationEventKind = "tried" | "kept" | "reverted" | "wrong" | "not_applicable" | "reopened";

/**
 * A narrower scope than the whole plant; an absent field is all of it. parts and lines list the parts and lines a
 * change was applied to when it was more than one (at most 20 each): the effect check compares a change on parts with
 * the parts that did not get it.
 */
export interface RecommendationScope {
  line?: string;
  part?: string;
  shift?: string;
  parts?: string[];
  lines?: string[];
}

/**
 * The effect check's comparison of the parts that got a change with those that did not, over the same windows (a
 * difference in differences, B3 T3): the verdict reads `did` and its interval.
 */
export interface RecommendationEffectComparison {
  treated: string[];
  untreated: string[];
  treated_before?: RecommendationEffectSide;
  treated_after?: RecommendationEffectSide;
  untreated_before?: RecommendationEffectSide;
  untreated_after?: RecommendationEffectSide;
  treated_rate: number;
  untreated_rate: number;
  did: number;
  ci_low: number;
  ci_high: number;
  level: number;
  castings_ci_low: number;
  castings_ci_high: number;
  past?: { spread: number; dates: number; disjoint: number; t: number };
  interval_from: "castings_over_phi" | "past_dates";
}

/**
 * The stretch of plant days a plant pinned as a tried change's baseline (a stretch it knows was stable), both
 * inclusive and before the change's day: the effect check reads it in place of the days just before the change.
 */
export interface RecommendationBaseline {
  from: string;
  to: string;
}

/** What the effect check says of a tried change: always since the change, never because of it. */
export type RecommendationVerdict = "too_early" | "improved" | "no_clear_change" | "worse" | "cant_tell";

/** One window the effect check read: its days that cast, castings and the targeted pieces over the parts compared. */
export interface RecommendationEffectSide {
  from: string;
  to: string;
  kind: "moving" | "pinned" | "after";
  days: number;
  cast: number;
  pieces: number;
  rate: number;
}

/** Something else that moved in the window the check read, named, never weighed. */
export interface RecommendationEffectConfound {
  kind: string;
  words: string;
  key?: string;
  day?: string;
}

/** The window a parameter's readings were counted against: its ends (open when absent) and its source. */
export interface RecommendationLeverWindow {
  lo?: number;
  hi?: number;
  source: string;
  by_grade?: boolean;
  /** The window changed from day to day within the stretch: the ends are its first day's. */
  varies?: boolean;
}

/** A window's days that read a parameter, and those inside its window (at least inside_at of their readings). */
export interface RecommendationLeverSide {
  days: number;
  inside: number;
  share: number;
  readings: number;
  outside: number;
}

/**
 * A tried change's effect (the effect check, platform-core/internal/twin/recommendation_effect.go; the Go mirror of
 * recommendationEffect): the targeted defect's rate within the same parts before and after, the verdict and its words.
 */
export interface RecommendationEffect {
  verdict: RecommendationVerdict;
  computed_at: string;
  engine: string;
  result: {
    /** The verdict's reason in plain words: the rates and the days so far, or why it cannot tell. */
    detail: string;
    defects: string[];
    label: string;
    part?: string;
    /** Every part the scope names, as typed. */
    scope_parts?: string[];
    from?: string;
    to?: string;
    days_after: number;
    days_needed: number;
    /** The days that cast the after window grows to: the "of" of "14 of 28 days". */
    days_target?: number;
    /** The after window is as long as it will get; until then an improved, worse or no clear change verdict is "so far". */
    complete: boolean;
    before?: RecommendationEffectSide;
    after?: RecommendationEffectSide;
    parts?: string[];
    change?: {
      overall: number;
      rate: number;
      mix: number;
      /** Each window's rate at the same (midpoint) mix of parts: what the verdict reads. */
      before_at_mix: number;
      after_at_mix: number;
      ci_low: number;
      ci_high: number;
      level: number;
      phi: number;
      phi_source: string;
      mix_shift: number;
      /** The castings' own interval (Newcombe on castings / phi); ci_low and ci_high are the wider of it and the past's. */
      castings_ci_low?: number;
      castings_ci_high?: number;
      /**
       * The same comparison at past dates where the change was not (the placebo in time): how far it moved on its own
       * (spread), how many past dates, how many of them side by side without sharing a day, and Student's t on those.
       */
      past?: { spread: number; dates: number; disjoint: number; t: number };
      /** Which interval set ci_low and ci_high: the castings' own, or the plant's past. */
      interval_from?: "castings_over_phi" | "past_dates";
    };
    signal?: {
      signal: "above" | "below" | "within" | "none";
      rate: number;
      low: number;
      high: number;
      method: string;
      sigma_z?: number;
      /** The lower limit fell at or below zero: it could never see a fall, so "improved" reads the interval alone. */
      low_open?: boolean;
    };
    lever?: {
      param: string;
      label: string;
      /** The window the baseline's days were read against; window_after the one since the change, when it changed. */
      window?: RecommendationLeverWindow;
      window_after?: RecommendationLeverWindow;
      /** The share of a day's readings inside its window from which the day counts as inside (0.9). */
      inside_at?: number;
      before?: RecommendationLeverSide;
      after?: RecommendationLeverSide;
      held?: boolean;
      why?: string;
    };
    confounds: RecommendationEffectConfound[];
    /** The comparison with the parts that did not get the change, when it was read (effect/v4). */
    comparison?: RecommendationEffectComparison;
  };
}

/** One recorded event (recommendations.Event). */
export interface RecommendationEvent {
  id: string;
  org_id: string;
  key: string;
  /** The recommendation as the person saw it. */
  text_shown: string;
  kind: RecommendationEventKind;
  /** The plant day the change applied from (YYYY-MM-DD); always set on a tried event. */
  plant_day: string | null;
  /** null for the whole plant. */
  scope: RecommendationScope | null;
  /** A tried change's pinned baseline; absent when it is compared with the days just before it. */
  baseline?: RecommendationBaseline;
  note: string;
  actor: string;
  /** The person's display name, else "a teammate" (never an email address); empty when the event has no actor. */
  actor_name: string;
  /** web, mobile, copilot:<thread id>, or interventions (the intervention log's write-through). */
  via: string;
  /** Null until the effect check fills it for a tried event. */
  effect: RecommendationEffect | null;
  /** On a kept or reverted state a brief or a study serves: the tried event it follows, its day and its effect. */
  tried?: { plant_day: string; effect: RecommendationEffect | null };
  created_at: string;
}

/** Each recommendation's latest event, by its key, as a brief or a study serves it. */
export type RecommendationStates = Record<string, RecommendationEvent>;

/**
 * What grew since a verdict, so the recommendation reopened (twin.VerdictEvidence): replicated (a later window bore
 * the pattern out again, window_from to window_to, plant days, the end exclusive) or new_grain (a test that did not
 * exist when the plant judged now supports it).
 */
export interface VerdictEvidence {
  rule: "replicated" | "new_grain";
  finding_id: string;
  /** The driver's condition, in the plant's words. */
  condition: string;
  grain: "lot" | "day" | "heat";
  /** The driver's grade, in words; absent for the whole plant. */
  stratum?: string;
  verdict: "supported" | "suggestive";
  window_from?: string;
  window_to?: string;
}

/**
 * The plant's standing verdict on a recommendation, as a brief serves it beside the document (L2,
 * twin.RecommendationVerdict): collapsed (shown folded in the overall brief, left out of a period's), or reopened (the
 * evidence grew materially since: shown again, with the verdict as a note). A wrong or not_applicable event on its key,
 * or a Knowledge-page dispute of its mechanism's finding (kind disputed).
 */
export interface RecommendationStanding {
  state: "collapsed" | "reopened";
  source: "event" | "dispute";
  kind: "wrong" | "not_applicable" | "disputed";
  /** Who said so: a display name, "a teammate", or "the plant". */
  by: string;
  at: string;
  reason: string;
  finding_id?: string;
  evidence?: VerdictEvidence[];
}

/** Each recommendation's standing verdict, by its key; a recommendation with none is absent. */
export type RecommendationVerdicts = Record<string, RecommendationStanding>;

/** POST /v1/twin/recommendation-events's body. */
export interface RecommendationEventCreate {
  /** The client's own uuid, so a retried post is one event (the same id for the same key and kind answers the stored one). */
  id?: string;
  key: string;
  text_shown: string;
  kind: RecommendationEventKind;
  plant_day?: string;
  scope?: RecommendationScope;
  /** A tried event's alone: the stretch to compare it with, in place of the days just before it. */
  baseline?: RecommendationBaseline;
  note?: string;
  via?: string;
}

/** The list's filters. */
export interface RecommendationEventFilter {
  key?: string;
  kind?: RecommendationEventKind;
  /** Leaves out the events whose key starts with this ("intervention:", the log's own). */
  excludeKeyPrefix?: string;
  /** Only the newest of the events the other filters pick for each key (its state; with a kind, its newest of that kind). */
  latest?: boolean;
  limit?: number;
  offset?: number;
}

/** The key of an intervention's write-through event: its own text, never a recommendation the platform showed. */
export const INTERVENTION_KEY_PREFIX = "intervention:";

export const recommendationEventsApi = {
  list: (f: RecommendationEventFilter = {}) => {
    const q = new URLSearchParams();
    if (f.key) q.set("key", f.key);
    if (f.kind) q.set("kind", f.kind);
    if (f.excludeKeyPrefix) q.set("exclude_key_prefix", f.excludeKeyPrefix);
    if (f.latest) q.set("latest", "true");
    if (f.limit) q.set("limit", String(f.limit));
    if (f.offset) q.set("offset", String(f.offset));
    const s = q.toString();
    return api.get<Paginated<RecommendationEvent>>(`/v1/twin/recommendation-events${s ? `?${s}` : ""}`);
  },
  create: (body: RecommendationEventCreate) => api.post<RecommendationEvent>("/v1/twin/recommendation-events", body),
};

export const RECOMMENDATION_EVENTS_KEY = ["recommendation-events"] as const;

/**
 * Who owns a recommendation and by when it is due (migration 0250; the Go mirror is
 * platform-core/internal/recommendations/assignments.go). owner is a member's users.id ("" for none), owner_name their
 * display name, else "a teammate" (never an email address); due a plant day ("" for none).
 */
export interface RecommendationAssignment {
  key: string;
  text_shown: string;
  owner: string;
  owner_name: string;
  due: string;
  set_by: string;
  set_by_name: string;
  set_at: string;
}

/** Each recommendation's assignment, by its key; one with none is absent. */
export type RecommendationAssignments = Record<string, RecommendationAssignment>;

/** A member an action may be assigned to: their users.id and name. */
export interface AssignablePerson {
  user_id: string;
  name: string;
}

/** GET /v1/twin/recommendation-assignments: newest first; people for a caller who may assign. */
export interface RecommendationAssignmentList {
  items: RecommendationAssignment[];
  people?: AssignablePerson[];
}

/** PUT /v1/twin/recommendation-assignments's body: both owner and due empty clears it. */
export interface RecommendationAssignmentSet {
  key: string;
  text_shown: string;
  owner: string;
  due: string;
}

export const recommendationAssignmentsApi = {
  list: () => api.get<RecommendationAssignmentList>("/v1/twin/recommendation-assignments"),
  set: (body: RecommendationAssignmentSet) => api.put<RecommendationAssignment>("/v1/twin/recommendation-assignments", body),
};

export const RECOMMENDATION_ASSIGNMENTS_KEY = ["recommendation-assignments"] as const;

/** The org's owners and due dates, by key, and the people an assigner picks from. */
export function useRecommendationAssignmentsQuery(enabled = true) {
  const live = useBriefLive();
  return useQuery({
    queryKey: RECOMMENDATION_ASSIGNMENTS_KEY,
    queryFn: recommendationAssignmentsApi.list,
    enabled: live && enabled,
    staleTime: 30_000,
  });
}

/** The list as a map by key. */
export function assignmentsByKey(list: RecommendationAssignmentList | undefined): RecommendationAssignments {
  const out: RecommendationAssignments = {};
  for (const a of list?.items ?? []) out[a.key] = a;
  return out;
}

/** Sets an owner and a due date, then reads the assignments and the action list again. */
export function useSetRecommendationAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: recommendationAssignmentsApi.set,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RECOMMENDATION_ASSIGNMENTS_KEY });
      void qc.invalidateQueries({ queryKey: ACTIONS_KEY });
    },
  });
}

/** The page the server sends at most, and how many pages a read follows: 1000 events, then it stops. */
export const EVENTS_PAGE = 200;
export const EVENTS_MAX_PAGES = 5;

/** Every event a filter picks, page by page (newest first), up to EVENTS_MAX_PAGES pages. */
export async function listAllRecommendationEvents(
  f: Omit<RecommendationEventFilter, "limit" | "offset"> = {},
): Promise<RecommendationEvent[]> {
  const out: RecommendationEvent[] = [];
  for (let page = 0; page < EVENTS_MAX_PAGES; page++) {
    const got = await recommendationEventsApi.list({ ...f, limit: EVENTS_PAGE, offset: page * EVENTS_PAGE });
    out.push(...got.items);
    if (got.items.length < EVENTS_PAGE) break;
  }
  return out;
}

/** The org's events, newest first. */
export function useRecommendationEventsQuery(f: RecommendationEventFilter = {}, enabled = true) {
  const live = useBriefLive();
  return useQuery({
    queryKey: [...RECOMMENDATION_EVENTS_KEY, f.key ?? "", f.kind ?? "", f.excludeKeyPrefix ?? "", f.latest ?? false, f.limit ?? 0, f.offset ?? 0],
    queryFn: () => recommendationEventsApi.list(f),
    enabled: live && enabled,
    staleTime: 30_000,
  });
}

/**
 * What a recording reads again: the briefs, the studies, the events and the action list, so every card shows the new
 * state wherever it stands (marked once, read everywhere: the 2026-10-06 next-phase plan, step 2).
 */
export const RECORD_REFRESHES = [
  PLANT_BRIEF_KEY,
  CADENCE_BRIEF_KEY,
  BRIEF_LIST_KEY,
  STUDY_KEY,
  RECOMMENDATION_EVENTS_KEY,
  ACTIONS_KEY,
] as const;

/** Records an event, then reads again what shows it (RECORD_REFRESHES). */
export function useRecordRecommendationEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: recommendationEventsApi.create,
    onSuccess: () => {
      for (const key of RECORD_REFRESHES) {
        void qc.invalidateQueries({ queryKey: key });
      }
    },
  });
}
